import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, Input, Modal, Progress, Radio, Select, Spin, Tag, Typography, message } from 'antd';
import MonthSelector from '../layout/MonthSelector';
import api from '../common/api';
import { useCashFlow } from '../common/CashFlowContext';

const fmt = (n) => new Intl.NumberFormat('ko-KR').format(n);

const SCOPE_SHARED = 'shared';
const SCOPE_MINE = 'mine';

function BudgetCard({ budget, scopeLabel, spent, onEdit }) {
  const limit = budget.amount || 0;
  const progress = limit > 0 ? Math.min((spent / limit) * 100, 100) : 0;
  const over = limit > 0 && spent > limit;

  return (
    <Card style={{ marginBottom: 12 }} bodyStyle={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div>
          <Typography.Text strong style={{ fontSize: 15 }}>{budget.category_name}</Typography.Text>
          <Tag color={budget.user_id == null ? 'blue' : 'purple'} style={{ marginLeft: 8 }}>{scopeLabel}</Tag>
        </div>
        <Button type="link" onClick={onEdit}>수정</Button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        <Typography.Text style={{ color: over ? '#E53935' : undefined }}>
          {fmt(spent)}원 사용
        </Typography.Text>
        <Typography.Text type="secondary">/ {fmt(limit)}원</Typography.Text>
      </div>
      <Progress
        percent={progress}
        showInfo={false}
        strokeColor={over ? '#E53935' : '#4A90D9'}
      />
      {over && (
        <Typography.Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
          {fmt(spent - limit)}원 초과
        </Typography.Text>
      )}
    </Card>
  );
}

function BudgetCreateModal({ open, categories, currentUser, existingKeys, onClose, onSubmit }) {
  const [categoryId, setCategoryId] = useState(null);
  const [scope, setScope] = useState(SCOPE_SHARED);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCategoryId(null);
      setScope(SCOPE_SHARED);
      setValue('');
    }
  }, [open]);

  const handleOk = async () => {
    if (categoryId == null) {
      message.error('카테고리를 선택해 주세요');
      return;
    }
    if (scope === SCOPE_MINE && !currentUser) {
      message.error('로그인된 사용자가 없어 \'내것\' 예산을 만들 수 없어요');
      return;
    }
    const userId = scope === SCOPE_MINE ? currentUser.id : null;
    const key = `${userId ?? 'null'}_${categoryId}`;
    if (existingKeys.has(key)) {
      message.error('이미 같은 범위의 예산이 있어요. 해당 카드의 \'수정\'을 눌러 주세요');
      return;
    }
    const amount = Number(String(value).replace(/,/g, ''));
    if (!amount || amount <= 0) {
      message.error('금액을 입력해 주세요');
      return;
    }
    setSaving(true);
    try {
      await onSubmit({ userId, categoryId, amount });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="예산 추가"
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>취소</Button>,
        <Button key="ok" type="primary" loading={saving} onClick={handleOk}>저장</Button>,
      ]}
      destroyOnClose
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>카테고리</Typography.Text>
          <Select
            value={categoryId}
            onChange={setCategoryId}
            placeholder="카테고리 선택"
            style={{ width: '100%' }}
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
          />
        </div>
        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>범위</Typography.Text>
          <Radio.Group value={scope} onChange={(e) => setScope(e.target.value)}>
            <Radio value={SCOPE_SHARED}>전체</Radio>
            <Radio value={SCOPE_MINE} disabled={!currentUser}>내것{currentUser ? ` (${currentUser.name})` : ''}</Radio>
          </Radio.Group>
        </div>
        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>금액</Typography.Text>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="금액 입력 (원)"
          />
        </div>
      </div>
    </Modal>
  );
}

function BudgetEditModal({ open, budget, onClose, onSubmit, onDelete }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && budget) setValue(String(budget.amount));
  }, [open, budget]);

  if (!budget) return null;

  const handleOk = async () => {
    const amount = Number(String(value).replace(/,/g, ''));
    if (!amount || amount <= 0) {
      message.error('금액을 입력해 주세요');
      return;
    }
    setSaving(true);
    try {
      await onSubmit(amount);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`${budget.category_name} 예산 수정`}
      onCancel={onClose}
      footer={[
        <Button key="del" danger loading={saving} onClick={handleDelete}>삭제</Button>,
        <Button key="cancel" onClick={onClose}>취소</Button>,
        <Button key="ok" type="primary" loading={saving} onClick={handleOk}>저장</Button>,
      ]}
      destroyOnClose
    >
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="금액 입력 (원)"
        autoFocus
      />
    </Modal>
  );
}

export default function BudgetScreen() {
  const { currentUser, yearMonth } = useCashFlow();
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, cRes, mRes] = await Promise.all([
        api.get('/budgets', { params: { year: yearMonth.year, month: yearMonth.month } }),
        api.get('/categories'),
        api.get('/stats/monthly', { params: { year: yearMonth.year, month: yearMonth.month } }),
      ]);
      setBudgets(bRes.data);
      setCategories(cRes.data);
      setMonthly(mRes.data);
    } finally {
      setLoading(false);
    }
  }, [yearMonth]);

  useEffect(() => { fetch(); }, [fetch]);

  const categorySpentMap = useMemo(() => {
    const map = {};
    (monthly?.by_category || []).forEach((c) => { map[c.category_id] = c; });
    return map;
  }, [monthly]);

  const spentFor = useCallback((budget) => {
    const cat = categorySpentMap[budget.category_id];
    if (!cat) return 0;
    if (budget.user_id == null) return cat.total_amount || 0;
    return budget.user_id === 1 ? (cat.user1_amount || 0) : (cat.user2_amount || 0);
  }, [categorySpentMap]);

  const sortedBudgets = useMemo(() => {
    return [...budgets].sort((a, b) => {
      const an = a.category_name || '';
      const bn = b.category_name || '';
      if (an !== bn) return an.localeCompare(bn, 'ko');
      const au = a.user_id == null ? 0 : 1;
      const bu = b.user_id == null ? 0 : 1;
      return au - bu;
    });
  }, [budgets]);

  const existingKeys = useMemo(() => {
    return new Set(budgets.map((b) => `${b.user_id ?? 'null'}_${b.category_id}`));
  }, [budgets]);

  const upsert = async ({ userId, categoryId, amount }) => {
    await api.put('/budgets', {
      user_id: userId,
      category_id: categoryId,
      year: yearMonth.year,
      month: yearMonth.month,
      amount,
    });
    await fetch();
  };

  const remove = async (id) => {
    await api.delete(`/budgets/${id}`);
    await fetch();
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <MonthSelector />
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <Button type="primary" onClick={() => setCreating(true)}>예산 추가</Button>
          </div>
          {sortedBudgets.length === 0 ? (
            <Empty description="등록된 예산이 없어요" />
          ) : (
            sortedBudgets.map((b) => (
              <BudgetCard
                key={b.id}
                budget={b}
                scopeLabel={b.user_id == null ? '전체' : `내것${currentUser && b.user_id === currentUser.id ? '' : ` (#${b.user_id})`}`}
                spent={spentFor(b)}
                onEdit={() => setEditing(b)}
              />
            ))
          )}
        </>
      )}
      <BudgetCreateModal
        open={creating}
        categories={categories}
        currentUser={currentUser}
        existingKeys={existingKeys}
        onClose={() => setCreating(false)}
        onSubmit={upsert}
      />
      <BudgetEditModal
        open={!!editing}
        budget={editing}
        onClose={() => setEditing(null)}
        onSubmit={(amount) => upsert({ userId: editing.user_id, categoryId: editing.category_id, amount })}
        onDelete={() => remove(editing.id)}
      />
    </div>
  );
}
